import type { HTMLAttributes, ReactNode } from 'react';

import { cx } from './cx.js';

export type TextTone = 'ink' | 'muted' | 'subtle' | 'accent' | 'gold' | 'flame' | 'danger';
export type TextVariant =
  | 'display'
  | 'title'
  | 'heading'
  | 'body'
  | 'label'
  | 'caption'
  | 'overline';

const variantClass: Record<TextVariant, string> = {
  // `display` is reserved for the numbers that carry the product identity —
  // Power Level and streak count. Nothing else earns this size.
  display: 'text-display leading-tight tracking-tight font-bold tabular-nums',
  title: 'text-2xl leading-tight tracking-tight font-semibold',
  heading: 'text-lg leading-snug font-semibold',
  body: 'text-base leading-normal font-regular',
  label: 'text-sm leading-snug font-medium',
  caption: 'text-xs leading-normal font-regular',
  overline: 'text-xs leading-snug font-semibold tracking-caps uppercase',
};

const toneClass: Record<TextTone, string> = {
  ink: 'text-ink',
  muted: 'text-muted',
  subtle: 'text-subtle',
  accent: 'text-accent',
  gold: 'text-gold',
  flame: 'text-flame',
  danger: 'text-danger',
};

export type TextProps = HTMLAttributes<HTMLElement> & {
  as?: 'span' | 'p' | 'h1' | 'h2' | 'h3' | 'h4' | 'strong' | 'label' | 'div';
  variant?: TextVariant;
  tone?: TextTone;
  /** Numerals align in columns — matters for stat tiles and history lists. */
  numeric?: boolean;
  children?: ReactNode;
};

export function Text({
  as: Tag = 'span',
  variant = 'body',
  tone = 'ink',
  numeric = false,
  className,
  children,
  ...rest
}: TextProps) {
  return (
    <Tag
      className={cx(variantClass[variant], toneClass[tone], numeric && 'tabular-nums', className)}
      {...rest}
    >
      {children}
    </Tag>
  );
}
